import { createTheme, Theme } from '@mui/material/styles';
import { shadowRootElement } from 'services/event-manager';
import { ThemeEnum } from 'types/market-types';

export const createDxpTheme = (appTheme: Theme, getRootFontSize: number, mode?: string): Theme => {
  const breakpointValues = {
    xs: 0,
    sm: 600,
    md: 992,
    lg: 1280,
    xl: 1920,
  };
  const paletteDefault = {
    background: {
      default: '#fff',
    },
    primary: {
      main: '#282830', // toyota black
      contrastText: '#fff',
      light: '#a8aaac',
      dark: '#282830',
    },
    secondary: {
      main: '#FF0022',
      light: '#282830',
      contrastText: '#FF0022',
    },
    text: {
      primary: '#282830', // new dark gray text
      secondary: '#595D60', // new light gray text
      light: '#6C7073',
      dark: '#282830',
    },
    grey: {
      200: '#f0f0f0', // grey light header background
      300: '#e4e4e4',
      400: '#a8aaac', // radio, checkbox color
      500: '#6C7073',
      600: '#585D5F', // grey dark input text color
      700: '#D9E1E3', // new grey
    },
    error: {
      main: '#FF0022',
    },
    action: {
      hover: '#d90022', // dark'ish toyota red on hover
    },
    info: {
      main: '#0072F0',
    },
    warning: {
      main: '#FF9900',
    },
  };
  const paletteBz = {
    background: {
      default: '#fff',
    },
    primary: {
      main: '#282830', // toyota black - change color
      contrastText: '#fff',
      light: '#003641',
      dark: '#003641',
    },
    secondary: {
      main: '#FF0022',
      light: '#46A4B0',
      contrastText: '#003641',
    },
    text: {
      primary: '#282830', // new dark gray text
      secondary: '#595D60', // new light gray text
      light: '#003641',
      dark: '#fff',
    },
    grey: {
      200: '#f0f0f0', // grey light header background
      300: '#e4e4e4',
      400: '#a8aaac', // radio, checkbox color
      500: '#6C7073',
      600: '#585D5F', // grey dark input text color
      700: '#D9E1E3', // new grey
    },
    error: {
      main: '#FF0022',
    },
    action: {
      hover: '#d90022', // dark'ish toyota red on hover
    },
    info: {
      main: '#0072F0',
    },
    warning: {
      main: '#FF9900',
    },
  };
  const paletteSpeedboat = {
    background: {
      default: '#fff',
    },
    primary: {
      main: '#001618', // toyota black - change color
      contrastText: '#fff',
      light: '#003641',
      dark: '#003641',
    },
    secondary: {
      main: '#A00008',
      light: '#003641',
      contrastText: '#003641',
    },
    text: {
      primary: '#001618', // new dark gray text
      secondary: '#595D60', // new light gray text
      light: '#003641',
      dark: '#fff',
    },
    grey: {
      200: '#f0f0f0', // grey light header background
      300: '#e4e4e4',
      400: '#a8aaac', // radio, checkbox color
      500: '#6C7073',
      600: '#585D5F', // grey dark input text color
      700: '#D9E1E3', // new grey for speedboat
      800: '#4C5253', // new grey for speedboat
    },
    error: {
      main: '#A00008',
    },
    action: {
      hover: '#d90022', // dark'ish toyota red on hover
    },
    info: {
      main: '#0072F0',
    },
    warning: {
      main: '#FF9900',
    },
  };
  const palette = mode === ThemeEnum.bz ? paletteBz : mode === ThemeEnum.speedboat ? paletteSpeedboat : paletteDefault;

  const shadows = {
    0: 'none',
    5: '0 2px 5px 0 #D7D7D7',
    10: '0 2px 10px 0 #D7D7D7',
  };

  return createTheme({
    ...appTheme,
    breakpoints: { values: breakpointValues },
    zIndex: { drawer: 1000 },
    palette,
    typography: {
      htmlFontSize: getRootFontSize,
      h2: {
        fontSize: '34px',
        lineHeight: '40px',
        textTransform: 'uppercase',
      },
      h5: {
        fontSize: '22px',
        lineHeight: '28px',
      },
    },
    components: {
      MuiPopover: {
        defaultProps: {
          container: shadowRootElement,
        },
      },
      MuiPopper: {
        defaultProps: {
          container: shadowRootElement,
        },
      },
      MuiModal: {
        defaultProps: {
          container: shadowRootElement,
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            display: 'none',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: palette.secondary.contrastText,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            '&.Mui-active': {
              '&$label': {
                fontFamily: 'ToyotaTypeBook',
                fontSize: '16px',
              },
            },
          },
          label: {
            color: '#6C7073',
            fontSize: '16px',
            fontFamily: 'ToyotaTypeBook',
            '&$alternativeLabel': {
              marginTop: '5px',
            },
          },
          iconContainer: {
            display: 'none',
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          horizontal: {
            paddingLeft: '0',
            paddingRight: '0',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            fontFamily: 'ToyotaTypeBook',
            border: `1px solid ${palette.grey[300]}`,
            background: palette.background.default,
            '&$underline:before': {
              border: 0,
            },
            '&$underline:after': {
              border: 0,
            },
            '&$underline:hover:not(.Mui-disabled):before': {
              border: 0,
            },
            '&$disabled': {
              background: palette.grey[300],
            },
          },
          formControl: {
            height: '56px',
            marginTop: '0 !important',
          },
          input: {
            marginTop: '25px',
            fontSize: '16px',
            fontFamily: 'ToyotaTypeBook',
            padding: '0 20px 11px 20px',
            position: 'relative',
            '&::placeholder': {
              opacity: 1,
            },
            color: palette.text.primary,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: 'ToyotaTypeBook',
            fontSize: '13px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            margin: 0,
            color: `${palette.grey[600]} !important`,
            zIndex: 1,
            pointerEvents: 'none',
            '&$error': {
              color: palette.grey[600],
            },
          },
          formControl: {
            position: 'relative',
            top: '13px',
            left: '20px',
            marginTop: '0',
            color: palette.grey[600],
            '&$shrink': {
              top: '20px',
            },
          },
          asterisk: {
            color: palette.secondary.contrastText,
            '&$error': {
              color: palette.secondary.main,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            '& *': {
              fontFamily: 'ToyotaTypeRegular',
              textIndent: 'unset',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            '& button *': {
              fontFamily: 'ToyotaTypeRegular',
            },
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&$focused [class*="MuiOutlinedInput-notchedOutline"]': {
              borderColor: appTheme.palette.common.black,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          rail: {
            color: palette.grey[200],
            opacity: 1,
          },
          thumb: {
            height: '36px',
            width: '36px',
            color: palette.background.default,
            top: '0px',
            marginLeft: '-18px',
            boxShadow: `${shadows[5]}`,
            '&$active': {
              boxShadow: `${shadows[10]}`,
            },
            '&:hover': {
              boxShadow: `${shadows[10]}`,
            },
          },
          mark: {
            backgroundColor: 'transparent',
          },
          markLabel: {
            top: '38px',
            color: palette.text.primary,
            fontFamily: 'ToyotaTypeRegular',
            fontSize: '13px',
            pointerEvents: 'none',
            '&[data-index="0"]': {
              left: '3% !important',
            },
            '&[data-index="1"]': {
              left: '96% !important',
            },
          },
          markActive: {
            backgroundColor: 'transparent',
          },
          markLabelActive: {
            color: palette.text.primary,
          },
        },
      },
    },
  });
};
